import { LabelLayoutOptionCallbackParams } from 'echarts';
import { DefaultLabelFormatterCallbackParams } from 'echarts/types/dist/option';
import { CallbackDataParams } from 'echarts/types/dist/shared';

import { NSCommons } from '@ruby/configs/common.constants';
import { EColors } from '@ruby/shared/enums/colors.enums';
import { EOrderFillDateInterval } from '@ruby/shared/enums/order-fill.enums';

export class NSOrderFillConstants {
  static readonly dateIntervals: Array<EOrderFillDateInterval> = [EOrderFillDateInterval.LAST_WEEK, EOrderFillDateInterval.LAST_WEEKS, EOrderFillDateInterval.LAST_MONTH,
    EOrderFillDateInterval.YEAR_TO_DATE, EOrderFillDateInterval.LAST_YEAR];

  static datePieConfigs = {
    title: {
      text: 'Totals',
      textAlign: 'left',
      textStyle: {
        fontSize: 24,
        lineHeight: 32
      }
    },
    tooltip: {
      trigger: 'item'
    },
    legend: {
      show: false
    },
    series: [
      {
        name: 'Totals',
        type: 'pie',
        radius: ['39%', '85%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderColor: EColors.WHITE,
          borderWidth: 8
        },
        animation: false,
        silent: true,
        stillShowZeroSum: false,
        label: {
          show: true,
          position: 'left',
          fontWeight: 'bold',
          fontFamily: 'Lato-regular',
          fontSize: 16,
          formatter: (amount: DefaultLabelFormatterCallbackParams) => amount?.value && amount.value > 0 ? NSCommons.numberValueDecimalFormatter(amount.value as number) : ''
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 40,
            fontWeight: 'bold'
          }
        },
        labelLine: {
          show: false
        },
        color: [EColors.CHART_GREEN, EColors.CHART_YELLOW, EColors.CHART_LIGHT_RED],
        height: 165,
        data: []
      }
    ]
  };

  static datePieNoConfigs = {
    ...NSOrderFillConstants.datePieConfigs.series,
    series: [{
      ...NSOrderFillConstants.datePieConfigs.series[0],
      name: 'No Configs',
      stillShowZeroSum: true,
      label: {
        ...NSOrderFillConstants.datePieConfigs.series[0].label,
        formatter: (amount: DefaultLabelFormatterCallbackParams) => amount?.value && amount.value > 0 ? NSCommons.numberValueDecimalFormatter(amount.value as number) : 0
      }
    }
    ]
  };

  static orderFillPieConfigs = {
    title: {
      text: 'Order Fill',
      textAlign: 'left',
      textStyle: {
        fontSize: 24,
        lineHeight: 32
      }
    },
    tooltip: {
      trigger: 'item'
    },
    legend: {
      show: false
    },
    series: [
      {
        name: 'Order Fill',
        type: 'pie',
        radius: ['40%', '85%'],
        avoidLabelOverlap: true,
        itemStyle: {
          borderColor: EColors.WHITE,
          borderWidth: 8
        },
        animation: false,
        silent: true,
        label: {
          show: true,
          position: 'center',
          fontWeight: 'bold',
          fontFamily: 'Lato-regular',
          fontSize: 20,
          lineHeight: 32
        },
        emphasis: {
          label: {
            show: false
          }
        },
        labelLine: {
          show: false
        },
        color: [EColors.CHART_GREEN, EColors.SUPER_LIGHT],
        data: []
      }
    ]
  };

  static weeklyLineConfig = {
    tooltip: {},
    xAxis: {
      axisTick: { show: false },
      data: [],
      axisLine: { show: true },
      splitLine: { show: true },
      splitArea: { show: false },
      axisLabel: {
        show: true,
        color: EColors.BLACK
      },
      lineStyle: { color: EColors.DARK_GREY }
    },
    yAxis: {
      axisTick: { show: false },
      axisLine: {
        onZero: true,
        show: true
      },
      lineStyle: { color: EColors.DARK_GREY },
      splitLine: { show: true },
      axisLabel: {
        show: true,
        color: EColors.BLACK
      }
    },
    grid: {
      top: 50,
      bottom: 40
    },
    series: [
      {
        name: 'Filled (within Request)',
        type: 'bar',
        stack: 'one',
        color: EColors.CHART_GREEN,
        data: [],
        barMaxWidth: 80,
        label: {
          show: true,
          position: 'inside',
          formatter: (params: CallbackDataParams) => params?.value !== 0 ? NSCommons.numberValueDecimalFormatter(params.value as number) : '',
          color: EColors.BLACK
        },
        labelLayout: () => ({
          hideOverLap: true
        })
      },
      {
        name: 'Filled (outside Request)',
        type: 'bar',
        stack: 'one',
        color: EColors.CHART_LIGHT_GREEN,
        data: [],
        barMaxWidth: 80,
        label: {
          show: true,
          position: 'inside',
          formatter: (params: CallbackDataParams) => params?.value !== 0 ? NSCommons.numberValueDecimalFormatter(params.value as number) : '',
          color: EColors.BLACK
        },
        labelLayout: () => ({
          hideOverLap: true
        })
      },
      {
        name: 'Accepted (Not Filled)',
        type: 'bar',
        stack: 'one',
        color: EColors.CHART_YELLOW,
        data: [],
        barMaxWidth: 80,
        label: {
          show: true,
          position: 'inside',
          formatter: (params: CallbackDataParams) => params?.value !== 0 ? NSCommons.numberValueDecimalFormatter(params.value as number) : '',
          color: EColors.BLACK
        },
        labelLayout: () => ({
          hideOverLap: true
        })
      },
      {
        name: 'Not Accepted',
        type: 'bar',
        stack: 'one',
        color: EColors.CHART_LIGHT_RED,
        data: [],
        barMaxWidth: 80,
        label: {
          show: true,
          position: 'inside',
          formatter: (params: CallbackDataParams) => params?.value !== 0 ? NSCommons.numberValueDecimalFormatter(params.value as number) : '',
          color: EColors.BLACK
        },
        labelLayout: () => ({
          hideOverLap: true
        })
      },
      {
        name: 'Requested',
        type: 'bar',
        stack: 'one',
        color: EColors.TRANSPARENT,
        tooltip: { show: false },
        legend: { show: false },
        barMaxWidth: 80,
        label: {
          show: true,
          position: 'bottom',
          color: EColors.MEDIUM_GRAY,
          fontFamily: 'Lato-Regular',
          offset: [0, -20],
          formatter: (params: CallbackDataParams) => NSCommons.numberValueDecimalFormatter(params.value as number)
        }
      }
    ]
  };

  static dailyLineConfig = {
    silent: false,
    tooltip: {},
    xAxis: {
      type: 'category',
      splitLine: { show: true },
      data: [],
      axisLine: { show: false },
      splitArea: { show: false },
      axisTick: { show: false },
      axisLabel: {
        show: true,
        color: EColors.BLACK
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        show: true,
        color: EColors.BLACK
      },
      min: 0
    },
    series: [
      {
        name: 'On Premise Rejected',
        type: 'bar',
        stack: 'one',
        color: EColors.CHART_LIGHTER_BLUE,
        data: [],
        label: {
          show: true,
          fontFamily: 'Lato-Semibold',
          color: EColors.BLACK,
          position: 'insideBottom',
          formatter: (params: CallbackDataParams) => params?.value !== 0 ? NSCommons.numberValueDecimalFormatter(params.value as number) : ''
        },
        labelLayout: (params: LabelLayoutOptionCallbackParams) => ({
          y: params.rect.y + 10,
          align: 'center',
          hideOverLap: true
        })
      },
      {
        name: 'On Premise',
        type: 'bar',
        stack: 'one',
        color: EColors.CHART_BLUE,
        data: [],
        label: {
          show: true,
          fontFamily: 'Lato-Semibold',
          color: EColors.BLACK,
          position: 'insideBottom',
          formatter: (params: CallbackDataParams) => params?.value !== 0 ? NSCommons.numberValueDecimalFormatter(params.value as number) : ''
        },
        labelLayout: (params: LabelLayoutOptionCallbackParams) => ({
          y: params.rect.y + 10,
          align: 'center',
          hideOverLap: true
        })
      },
      {
        name: 'Filled Empty Space',
        type: 'bar',
        stack: 'two',
        color: EColors.TRANSPARENT,
        data: [],
        tooltip: {
          show: false
        },
        itemStyle: {
          borderColor: 'transparent',
          color: 'transparent'
        }
      },
      {
        name: 'Filled Outside Request',
        type: 'bar',
        stack: 'two',
        color: EColors.CHART_LIGHT_GREEN,
        data: [],
        label: {
          show: true,
          fontFamily: 'Lato-Semibold',
          color: EColors.BLACK,
          position: 'insideBottom',
          formatter: (params: CallbackDataParams) => params?.value !== 0 ? NSCommons.numberValueDecimalFormatter(params.value as number) : ''
        },
        labelLayout: (params: LabelLayoutOptionCallbackParams) => ({
          y: params.rect.y + 10,
          align: 'center',
          hideOverlap: true
        })
      },
      {
        name: 'Filled Inside Request',
        type: 'bar',
        stack: 'two',
        color: EColors.CHART_GREEN,
        data: [],
        label: {
          show: true,
          fontFamily: 'Lato-Semibold',
          color: EColors.BLACK,
          position: 'insideBottom',
          formatter: (params: CallbackDataParams) => params?.value !== 0 ? NSCommons.numberValueDecimalFormatter(params.value as number) : ''
        },
        labelLayout: (params: LabelLayoutOptionCallbackParams) => ({
          y: params.rect.y + 10,
          align: 'center',
          hideOverlap: true
        })
      },
      {
        name: 'Release Loaded Empty Space',
        type: 'bar',
        stack: 'three',
        color: EColors.TRANSPARENT,
        data: [],
        tooltip: {
          show: false
        },
        itemStyle: {
          borderColor: 'transparent',
          color: 'transparent'
        }
      },
      {
        name: 'Release Loaded',
        type: 'bar',
        stack: 'three',
        color: EColors.CHART_LIGHT_BLUE,
        data: [],
        label: {
          show: true,
          fontFamily: 'Lato-Semibold',
          color: EColors.BLACK,
          position: 'insideBottom',
          formatter: (params: CallbackDataParams) => params?.value !== 0 ? `-${ NSCommons.numberValueDecimalFormatter(params.value as number) }` : ''
        },
        labelLayout: (params: LabelLayoutOptionCallbackParams) => ({
          y: params.rect.y + 10,
          align: 'center',
          hideOverlap: true
        })
      },
      {
        name: 'Requested',
        type: 'bar',
        stack: 'two',
        color: EColors.TRANSPARENT,
        tooltip: { show: false },
        legend: { show: false },
        emphasis: { show: false },
        barGap: '7%',
        barCategoryGap: '10%',
        label: {
          show: true,
          position: 'bottom',
          color: EColors.MEDIUM,
          fontFamily: 'Lato-Regular',
          fontSize: 14,
          distance: 0,
          offset: [0, -20],
          formatter: (params: CallbackDataParams) => `- ${ NSCommons.numberValueDecimalFormatter(params.value as number) } -`
        }
      }
    ]
  };
}
